<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { browseProducts } from '@/api/algolia';
import ToastNotification from '@/components/base/ToastNotification.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import BaseCustomizer from '@/components/customizer/BaseCustomizer.vue';
import BuyBlock from '@/components/customizer/mix/BuyBlock.vue';
import { deriveRelativeRange, VolumetricIngredient } from '@/lib/customizers/ingredients';
import { MIX_PRICE_MULTIPLIER, MixCustomizer, PARTS_LIMIT } from '@/lib/customizers/mixes';
import { isDefined } from '@/utils/isDefined';

const route = useRoute();
const router = useRouter();

const mixCustomizer = reactive(new MixCustomizer());

const ingredients = ref<VolumetricIngredient[]>([]);
const totalPrice = computed(() => mixCustomizer.calculateTotal());
const hashedCode = computed(() => mixCustomizer.encodeCustomizerSelections());

const notificationMessage = ref<string>();

onMounted(async () => {
  const products = await browseProducts({
    filters: 'traits:inMix',
  });

  const relativePriceRange = deriveRelativeRange(products, MIX_PRICE_MULTIPLIER);
  ingredients.value = products
    .filter((product) => !product.outOfStock)
    .map((product) => {
      try {
        const ingredient = VolumetricIngredient.fromAlgoliaVariant(
          product,
          relativePriceRange,
          MIX_PRICE_MULTIPLIER,
        );
        return ingredient;
      } catch (e) {
        return undefined;
      }
    })
    .filter(isDefined);

  mixCustomizer.prefillSelections(ingredients.value, route.query.code as string);
});

const setNotification = (message: string) => {
  notificationMessage.value = message;
};

const handleAddIngredient = (productKey: string) => {
  const addedIngredient = ingredients.value.find((ing) => ing.productKey === productKey);
  const alreadyInMix = mixCustomizer.selections.find((s) => s?.productKey === productKey);

  if (!addedIngredient) {
    return;
  }

  if (!alreadyInMix) {
    if (mixCustomizer.selections.filter(isDefined).length === mixCustomizer.ingredientLimits.max) {
      setNotification(
        `You've reached the max of ${mixCustomizer.ingredientLimits.max} ingredients`,
      );
      return;
    }
  } else if (alreadyInMix.parts === PARTS_LIMIT) {
    setNotification(`You've reached the max of ${PARTS_LIMIT} parts per ingredient`);
    return;
  }

  const parts = alreadyInMix ? alreadyInMix.parts + 1 : 1;
  mixCustomizer.setSelection(addedIngredient, parts);
};

const handleRemoveIngredient = (productKey: string, removeAll?: boolean) => {
  const ingredientToDecrease = mixCustomizer.selections.find((s) => s?.productKey === productKey);

  if (ingredientToDecrease) {
    const ingredient = ingredients.value.find(
      (ing) => ing.productKey === ingredientToDecrease.productKey,
    );

    if (ingredientToDecrease.parts - 1 === 0 || removeAll) {
      setNotification(`${ingredient!.name} has been removed from your mix`);
    }

    mixCustomizer.setSelection(ingredient!, removeAll ? 0 : ingredientToDecrease.parts - 1);
  }
};

watch(hashedCode, () => {
  if (route.query.code !== hashedCode.value) {
    router.replace({ query: { ...route.query, code: hashedCode.value } });
  }
});
</script>

<template>
  <BaseCustomizer
    v-slot="{ handleOpenIngredientsModal }"
    :ingredients="ingredients"
    :maxIngredients="mixCustomizer.ingredientLimits.max"
    mode="mix"
    :selections="mixCustomizer.selections"
    @add-ingredient="handleAddIngredient"
    @remove-ingredient="handleRemoveIngredient"
  >
    <BuyBlock
      v-model:weight="mixCustomizer.weight"
      v-model:quantity="mixCustomizer.quantity"
      :selections="mixCustomizer.selections"
      :total="totalPrice"
      @add-ingredient="handleAddIngredient"
      @open-modal="handleOpenIngredientsModal"
      @remove-ingredient="handleRemoveIngredient"
      @show-notification="setNotification"
    />
  </BaseCustomizer>
  <ToastNotification :show="!!notificationMessage" @hide="notificationMessage = ''">
    <BaseBodyText>{{ notificationMessage }}</BaseBodyText>
  </ToastNotification>
</template>
